/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import { RegisteredPlayer, Team } from '@sportninja/common/types/Registration'

// Enhanced useDebounce hook with performance optimization
const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Avoid setting timeout if value hasn't changed
    if (value === debouncedValue) return

    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay, debouncedValue])

  return debouncedValue
}

interface UseSearchAndFilterProps {
  divisions: RegistrationTableDivision[]
  setExpandedRows: (rows: string[]) => void
}

interface UseSearchAndFilterReturn {
  searchText: string
  setSearchText: (text: string) => void
  selectedDivision: string
  setSelectedDivision: (division: string) => void
  getFilteredTeams: (teams: Team[]) => Team[]
  getFilteredDivisions: () => RegistrationTableDivision[]
  openDivisions: { [key: number]: boolean }
  setOpenDivisions: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >
  allExpanded: boolean
  setAllExpanded: React.Dispatch<React.SetStateAction<boolean>>
  getFilteredPlayers: (players: RegisteredPlayer[]) => RegisteredPlayer[]
  onlyBuckets: boolean
  setOnlyBuckets: React.Dispatch<React.SetStateAction<boolean>>
  selectedStatuses: number[]
  setSelectedStatuses: React.Dispatch<React.SetStateAction<number[]>>
  shouldExpandOnMount: boolean
  onlyDeferredPayments: boolean
  setOnlyDeferredPayments: React.Dispatch<React.SetStateAction<boolean>>
}

// Default statuses (all except Cancelled)
const defaultStatuses = [1, 2, 3, 4] // Submitted, Pending, Approved, Approved w/ Conditions

export const useSearchAndFilter = ({
  divisions,
  setExpandedRows,
}: UseSearchAndFilterProps): UseSearchAndFilterReturn => {
  const [searchText, setSearchText] = useState('')
  const [selectedDivision, setSelectedDivision] = useState<string>('all')
  const [openDivisions, setOpenDivisions] = useState<{
    [key: number]: boolean
  }>({})
  const [allExpanded, setAllExpanded] = useState(false)
  const savedExpandedState = localStorage.getItem(
    'registrationTableAllExpanded'
  )
  const initialExpandedState = useRef(
    savedExpandedState ? JSON.parse(savedExpandedState) : false
  )

  const [onlyBuckets, setOnlyBuckets] = useState(false)
  const [onlyDeferredPayments, setOnlyDeferredPayments] = useState(false)
  const [selectedStatuses, setSelectedStatuses] =
    useState<number[]>(defaultStatuses)
  const prevSelectedStatuses = useRef<number[]>([...selectedStatuses])
  const isFirstRender = useRef(true)
  const isFirstStatusChange = useRef(true)
  const hasInitialized = useRef(false)

  // Save allExpanded state to localStorage whenever it changes
  useEffect(() => {
    if (!hasInitialized.current) {
      return
    }
    try {
      localStorage.setItem(
        'registrationTableAllExpanded',
        JSON.stringify(allExpanded)
      )
    } catch (e) {
      console.error('Error saving allExpanded state to localStorage', e)
    }
  }, [allExpanded])

  useEffect(() => {
    if (isFirstRender.current) {
      setOpenDivisions(
        divisions.reduce((acc, _, index) => {
          acc[index] = false
          return acc
        }, {})
      )
      isFirstRender.current = false
      hasInitialized.current = true
    }
  }, [divisions])

  // Return the initial state through the hook's return value
  const shouldExpandOnMount = initialExpandedState.current

  // Use increased debounce delay for better performance (800ms instead of 500ms)
  const debouncedSearchText = useDebounce(searchText, 800)

  // Memoize the lowercase search text to avoid repeated conversions
  const searchLower = useMemo(
    () => debouncedSearchText.toLowerCase(),
    [debouncedSearchText]
  )

  // Memoize the status filter function
  const filterByStatus = useCallback(
    (item: { registration_status: number }) =>
      selectedStatuses.length === 0 ||
      selectedStatuses.includes(item.registration_status),
    [selectedStatuses]
  )

  // Memoize the search text filter function
  const filterBySearchText = useCallback(
    (item: {
      name_first?: string
      name_last?: string
      name_full?: string
      name?: string
      player_id?: string
      id?: string
      email?: string
    }) => {
      if (!searchLower) {
        return true
      }
      return (
        item.name?.toLowerCase().includes(searchLower) || // For division names
        item.name_first?.toLowerCase().includes(searchLower) ||
        item.name_last?.toLowerCase().includes(searchLower) ||
        item.name_full?.toLowerCase().includes(searchLower) ||
        item.player_id?.toLowerCase().includes(searchLower) ||
        item?.email?.toLowerCase().includes(searchLower) ||
        item.id?.toLowerCase().includes(searchLower)
      )
    },
    [searchLower]
  )

  // Set all divisions that have buckets to open if onlyBuckets is true
  useEffect(() => {
    if (onlyBuckets) {
      const newOpenDivisions = divisions.reduce((acc, division, index) => {
        if (division.teams?.some((team) => team.is_bucket)) {
          acc[index] = true
        }
        return acc
      }, {})
      setOpenDivisions((prev) => ({ ...prev, ...newOpenDivisions }))
    }
  }, [divisions, onlyBuckets])

  // Set all divisions that have deferred payments to open if onlyDeferredPayments is true
  useEffect(() => {
    if (onlyDeferredPayments) {
      const newOpenDivisions = divisions.reduce((acc, division, index) => {
        if (division.teams?.some((team) => team.defer_payment)) {
          acc[index] = true
        }
        if (
          division.teams?.some((team) =>
            team.registered_players?.some((player) => player.defer_payment)
          )
        ) {
          acc[index] = true
        }
        return acc
      }, {})
      setOpenDivisions((prev) => ({ ...prev, ...newOpenDivisions }))
    }
  }, [divisions, onlyDeferredPayments])

  // If one division is selected, set the first division to open
  useEffect(() => {
    if (selectedDivision !== 'all') {
      setOpenDivisions({ [0]: true })
    }
    // else {
    //   console.log('Chamou aqui2', selectedDivision)
    //   setOpenDivisions(
    //     divisions.reduce((acc, _, index) => {
    //       acc[index] = false
    //       return acc
    //     }, {})
    //   )
    //   setAllExpanded(false)
    // }
  }, [selectedDivision, divisions])

  // If there is a search text, and there are filtered players, we should expand the team rows that have players
  useEffect(() => {
    if (debouncedSearchText && debouncedSearchText.length > 0) {
      const filteredPlayers = getFilteredPlayers(
        divisions.flatMap((division) =>
          division.teams?.flatMap((team) => team.registered_players || [])
        )
      )
      if (filteredPlayers.length > 0) {
        const teamIds = filteredPlayers.map((player) => player.team_roster_id)
        setExpandedRows(teamIds.filter((id) => id !== undefined) as string[])
      }
    }
  }, [debouncedSearchText, divisions])

  // If the status filter is changed, check the diviions to see if they have teams or players that match the status
  useEffect(() => {
    if (isFirstStatusChange.current && divisions?.length > 0) {
      isFirstStatusChange.current = false
      return
    }
    if (selectedStatuses.length > 0 && divisions?.length > 0) {
      // If the selected statuses are the same as the previous selected statuses, don't filter the divisions
      if (prevSelectedStatuses?.current?.length === selectedStatuses?.length) {
        return
      }
      prevSelectedStatuses.current = selectedStatuses
      const filteredDivisions = divisions.filter((division) =>
        division.teams?.some(
          (team) =>
            selectedStatuses.includes(team.registration_status) ||
            team.registered_players?.some((player) =>
              selectedStatuses.includes(player.registration_status)
            )
        )
      )
      setOpenDivisions(
        filteredDivisions.reduce((acc, _, index) => {
          acc[index] = true
          return acc
        }, {})
      )
      // Filter the divisions to only include those that have players with the selected statuses. If so, open the team rows
      const hasPlayersWithSelectedStatuses = filteredDivisions?.filter(
        (division) =>
          division.teams?.some((team) =>
            team.registered_players?.some((player) =>
              selectedStatuses.includes(player.registration_status)
            )
          )
      )
      if (hasPlayersWithSelectedStatuses?.length > 0) {
        const teamIds = hasPlayersWithSelectedStatuses.flatMap((division) =>
          division.teams?.map((team) => team.team_roster_id)
        )
        setExpandedRows(teamIds.filter((id) => id !== undefined) as string[])
      }
      setAllExpanded(true)
    }
  }, [divisions, selectedStatuses])

  // Optimize getFilteredTeams with memoized filters
  const getFilteredTeams = useCallback(
    (teams: Team[]) => {
      if (!teams) {
        return []
      }

      return teams
        .filter((team) => {
          // Apply status filter
          const teamStatusMatches = filterByStatus(team)
          const hasMatchingPlayers =
            team.registered_players?.some(filterByStatus)
          return teamStatusMatches || hasMatchingPlayers
        })
        .filter((team) => {
          // Apply bucket filter
          if (onlyBuckets) {
            return team.is_bucket
          }
          return true
        })
        .filter((team) => {
          // Apply deferred payment filter
          if (onlyDeferredPayments) {
            // First check if there is a player with a deferred payment
            const hasPlayerWithDeferredPayment = team.registered_players?.some(
              (player) => player.defer_payment
            )
            // Then check if the team has a deferred payment
            const isTeamDeferredPaymentMatch = team.defer_payment
            return hasPlayerWithDeferredPayment || isTeamDeferredPaymentMatch
          }
          return true
        })
        .filter((team) => {
          // Apply search text filter
          if (!searchLower) {
            return true
          }
          const isRegistrationIdMatch = team?.id
            ?.toLowerCase()
            .includes(searchLower)
          const idMatch = team?.team_id?.toLowerCase().includes(searchLower)
          const teamNameMatch = team?.name_full
            .toLowerCase()
            .includes(searchLower)
          const emailMatch = team?.email?.toLowerCase().includes(searchLower)
          const hasMatchingPlayer =
            team.registered_players?.some(filterBySearchText)
          const hasMatchingPlayerWithDeferredPayment =
            team.registered_players?.some(
              (player) => player.defer_payment && filterBySearchText(player)
            )
          return (
            emailMatch ||
            teamNameMatch ||
            hasMatchingPlayer ||
            idMatch ||
            isRegistrationIdMatch ||
            hasMatchingPlayerWithDeferredPayment
          )
        })
        .map((team) => ({
          ...team,
          key: `${team.team_roster_id}`,
        }))
    },
    [
      filterByStatus,
      filterBySearchText,
      onlyBuckets,
      onlyDeferredPayments,
      searchLower,
    ]
  )

  // Optimize getFilteredPlayers with memoized filters
  const getFilteredPlayers = useCallback(
    (players: RegisteredPlayer[]) => {
      if (!players) {
        return []
      }

      return players
        .filter(filterByStatus)
        .filter((player) => !searchLower || filterBySearchText(player))
        .filter((player) => {
          // Apply deferred payment filter
          if (onlyDeferredPayments) {
            return player.defer_payment
          }
          return true
        })
        .map((player) => ({
          ...player,
          key: player.uid,
        }))
    },
    [filterByStatus, filterBySearchText, onlyDeferredPayments, searchLower]
  )

  // Optimize getFilteredDivisions with memoized filters
  const getFilteredDivisions = useCallback(() => {
    const filterDivision = (division: RegistrationTableDivision) => {
      // If there's no search text, always show the division
      if (!searchLower) {
        return true
      }

      // Check if division name matches the search text
      if (division.name?.toLowerCase().includes(searchLower)) {
        return true
      }

      // Check if division id matches the search text
      if (division?.id?.toLowerCase().includes(searchLower)) {
        return true
      }

      // check if the division?.registration?.id matches the search text
      if (division?.registration?.id?.toLowerCase().includes(searchLower)) {
        return true
      }

      // If there's a search text, only show divisions with matching results
      const filteredTeams = getFilteredTeams(division.teams || [])
      const allPlayers =
        division.teams?.flatMap((team) => team.registered_players || []) || []
      const filteredPlayers = getFilteredPlayers(allPlayers)
      return filteredTeams.length > 0 || filteredPlayers.length > 0
    }

    return selectedDivision === 'all'
      ? divisions.filter(filterDivision)
      : divisions.filter(
          (division) =>
            division.id === selectedDivision && filterDivision(division)
        )
  }, [
    divisions,
    selectedDivision,
    getFilteredTeams,
    getFilteredPlayers,
    searchLower,
  ])

  return {
    searchText,
    setSearchText,
    selectedDivision,
    setSelectedDivision,
    getFilteredTeams,
    getFilteredDivisions,
    openDivisions,
    setOpenDivisions,
    allExpanded,
    setAllExpanded,
    getFilteredPlayers,
    onlyBuckets,
    setOnlyBuckets,
    onlyDeferredPayments,
    setOnlyDeferredPayments,
    selectedStatuses,
    setSelectedStatuses,
    shouldExpandOnMount,
  }
}
